import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import {
  useMeProfileQuery,
  usersKeys,
} from "@/modules/users/presentation/api/queries";
import { profileApi } from "../api/client";
import { useAuthState } from "../model/useAuthState";
import { ProfileAssignmentHistoryTable } from "../ui/ProfileAssignmentHistoryTable";
import { ProfileEmploymentSection } from "../ui/ProfileEmploymentSection";
import { ProfileForm } from "../ui/ProfileForm";
import { ProfileKpiResultsTable } from "../ui/ProfileKpiResultsTable";

export function ProfilePage() {
  const {
    user,
    refetch,
    hasPermission,
    hasAnyPermission,
    isLoading: authLoading,
  } = useAuthState();
  const qc = useQueryClient();
  const {
    data: profile,
    isPending: profileLoading,
    isError: profileError,
  } = useMeProfileQuery({ enabled: Boolean(user?.id) });
  const [submitting, setSubmitting] = useState(false);

  const canViewJd = hasPermission(Permissions.jd.read);
  const canViewKpiDashboard = hasAnyPermission([
    Permissions.kpi.read,
    Permissions.mgmt_okr_kpi.read,
  ]);
  const canViewPromotionRecords = hasPermission(
    Permissions.hr_promotion.record_read,
  );
  const nav = useNavigate();

  const displayEmail = profile?.user.email ?? user?.email ?? "—";
  const displayName = profile?.user.name ?? user?.name ?? "";

  return (
    <>
      <Header />

      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ໂປຣໄຟລ໌ຂອງຂ້ອຍ</h2>
            <p className="text-muted-foreground">
              ຈັດການບັນຊີ ແລະ ເບິ່ງຂໍ້ມູນການເຮັດວຽກໃນອົງກອນ
            </p>
          </div>
        </div>

        {authLoading || !user?.id ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດບັນຊີ…</p>
        ) : profileError ? (
          <p className="text-destructive text-sm">
            ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໂປຣໄຟລ໌ — ລອງຣີເຟຣຊ ຫຼື ລອງໃໝ່ພາຍຫຼັງ
          </p>
        ) : profileLoading || !profile ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດຂໍ້ມູນໂປຣໄຟລ໌…</p>
        ) : (
          <>
            <ProfileEmploymentSection
              reportingLine={profile.reportingLine}
              employment={profile.employment}
              jd={profile.jd}
              currentSalary={profile.currentSalary}
              kpiSummary={profile.kpiSummary}
              canViewJd={canViewJd}
              canViewKpiDashboard={canViewKpiDashboard}
            />

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>ປະຫວັດຕຳແໜ່ງ</CardTitle>
              </CardHeader>
              <CardContent>
                <ProfileAssignmentHistoryTable
                  rows={profile.assignmentHistory}
                />
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>KPI (ລາຍລະອຽດ)</CardTitle>
              </CardHeader>
              <CardContent>
                <ProfileKpiResultsTable rows={profile.kpiResults} />
              </CardContent>
            </Card>

            {canViewPromotionRecords ? (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>ເສັ້ນທາງການເຕີບໂຕ</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      nav({
                        to: "/app/hr/promotion/records",
                        search: {
                          offset: 0,
                          limit: 20,
                          sort: undefined,
                          employeeId: user.id,
                          periodYear: undefined,
                          periodMonth: undefined,
                          positionId: undefined,
                          search: undefined,
                        },
                      })
                    }
                  >
                    ເບິ່ງບັນທຶກການເລື່ອນຕຳແໜ່ງຂອງຂ້ອຍ
                  </Button>
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle>ບັນຊີ ແລະ ການຕິດຕໍ່</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-muted-foreground text-sm">
                    ອີເມວ (ແກ້ໄຂໄດ້ເທົ່ານັ້ນໂດຍຜູ້ດູແລລະບົບ)
                  </p>
                  <p className="font-medium">{displayEmail}</p>
                </div>

                <ProfileForm
                  key={`${profile.user.id}-${profile.user.updatedAt?.toString?.() ?? ""}`}
                  initialValues={{
                    name: displayName,
                    phone: profile.user.phoneNumber ?? "",
                    imageKey: profile.user.image ?? null,
                  }}
                  submitting={submitting}
                  onSubmit={async (vals) => {
                    setSubmitting(true);
                    try {
                      const fd = new FormData();
                      fd.append("name", vals.name);
                      fd.append("phoneNumber", vals.phone ?? "");
                      if (vals.password) fd.append("password", vals.password);
                      if (vals.imageKey === null) {
                        fd.append("imageDelete", "1");
                      } else if (vals.imageKey) {
                        fd.append("imageKey", vals.imageKey);
                      }
                      await profileApi.update(fd);
                      toast.success("ອັບເດດໂປຣໄຟລ໌ສຳເລັດ");
                      await qc.invalidateQueries({
                        queryKey: usersKeys.meProfile,
                      });
                      refetch();
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </Main>
    </>
  );
}
